import { PositionGeocode } from '@/domain/position-geocode/position-geocode.entity';
import { calculateDistanceBetweenCoordinates } from '@/domain/position-geocode/utils/calculate-distance/calculate-distance';
import { createAppAsyncThunk } from '@/domain/store.thunk';

import { TourEvent } from '../entities/project-tour-ontour-event.entity';
import { TourTravel } from '../entities/project-tour-ontour-travel.entity';
import {
  selectProjectTourOnTourEvents,
  selectProjectTourOnTourTravels,
} from '../project-tour-ontour.selectors';
import { getTourTravelsLinkedToTourEvent } from '../utils/get-tour-travels-linked-to-tour-event';
import { editTourTravel } from './edit-tour-travel.action';
import { getTourEvents } from './get-tour-events.action';

type Params = {
  tourEvent: TourEvent;
};

type Result = void;

export const updateTravelDistanceForLinkedTravelsIfNeeded = createAppAsyncThunk(
  'projectTourOntour/updateTravelDistanceForLinkedTravelsIfNeeded',
  async (params: Params, { dispatch, getState }): Promise<Result> => {
    await dispatch(getTourEvents({ sequenceId: params.tourEvent.sequenceId })); // be sure to use updated tour-event
    const tourEvents = selectProjectTourOnTourEvents(getState());
    const tourTravels = selectProjectTourOnTourTravels(getState());
    const linkedTourTravels = getTourTravelsLinkedToTourEvent(
      tourTravels,
      params.tourEvent.id,
    );
    for (const tourTravel of linkedTourTravels) {
      const newTravelDistance = await computeNewTravelDistance(
        tourEvents,
        tourTravel,
      );
      dispatch(
        editTourTravel({
          tourTravel: { ...tourTravel, travelDistance: newTravelDistance },
        }),
      );
    }
  },
);

const computeNewTravelDistance = async (
  tourEvents: TourEvent[],
  tourTravel: TourTravel,
) => {
  switch (tourTravel.connexionTypeWithEvent) {
    case 'incoming': {
      const fromPosition = tourTravel.fromPosition;
      const toEvent = tourEvents.find(
        (item) => item.id === tourTravel.toEventId,
      );
      if (!fromPosition || !toEvent) {
        throw new Error('fromPosition or toEvent is undefined');
      }
      return await computeAndSetDistance_incoming(fromPosition, toEvent);
    }
    case 'direct': {
      const fromEvent = tourEvents.find(
        (item) => item.id === tourTravel.fromEventId,
      );
      const toEvent = tourEvents.find(
        (item) => item.id === tourTravel.toEventId,
      );
      if (!fromEvent || !toEvent) {
        throw new Error('fromEvent or toEvent is undefined');
      }
      return await computeAndSetDistance_direct(fromEvent, toEvent);
    }
    case 'outgoing': {
      const fromEvent = tourEvents.find(
        (item) => item.id === tourTravel.fromEventId,
      );
      const toPosition = tourTravel.toPosition;
      if (!fromEvent || !toPosition) {
        throw new Error('fromEvent or toPosition is undefined');
      }
      return await computeAndSetDistance_outgoing(fromEvent, toPosition);
    }
  }
};

const computeAndSetDistance_incoming = async (
  fromPosition: PositionGeocode,
  toEvent: TourEvent,
) => {
  const from = fromPosition?.coordinates;
  const to = toEvent?.position?.coordinates;
  const distance = await calculateDistanceBetweenCoordinates(from, to);
  return distance;
};

const computeAndSetDistance_outgoing = async (
  fromEvent: TourEvent,
  toPosition: PositionGeocode,
) => {
  const from = fromEvent?.position?.coordinates;
  const to = toPosition?.coordinates;
  const distance = await calculateDistanceBetweenCoordinates(from, to);
  return distance;
};

const computeAndSetDistance_direct = async (
  fromEvent: TourEvent,
  toEvent: TourEvent,
) => {
  const from = fromEvent?.position?.coordinates;
  const to = toEvent?.position?.coordinates;
  const distance = await calculateDistanceBetweenCoordinates(from, to);
  return distance;
};
